import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { projectService } from '../services/projectService';
import ConfirmDialog from './ConfirmDialog';
import './ProjectPhotoGallery.css';
import { newUuid } from '../utils/newUuid';

/**
 * Project photo gallery — web counterpart of the iOS
 * `PhotoGalleryViewController`.
 *
 * Data model is set by iOS (see `Housfully/Housfully/Models/Project.swift`):
 *   - `project.galleryPhotos: { url, createdAt }[]`
 *   - `project.imageUrl: string` (cover photo)
 *
 * Storage layout (iOS — `PhotoGalleryViewController.uploadSinglePhoto`):
 *   - Full: `projects/{projectId}/gallery/{photoId}.jpg`
 *   - Thumb: `projects/{projectId}/gallery/{photoId}_400.jpg`
 *
 * Web mirrors the same convention so iOS-uploaded thumbnails keep working
 * and web-uploaded photos render correctly on iOS.
 */
function ProjectPhotoGallery({ project, projectId, canEdit, onProjectUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [pendingDeleteUrl, setPendingDeleteUrl] = useState(null);
  const [busyAction, setBusyAction] = useState(null); // for hover-revealed buttons
  const fileInputRef = useRef(null);

  const galleryPhotos = useMemo(() => {
    const photos = Array.isArray(project?.galleryPhotos)
      ? project.galleryPhotos
      : [];
    return [...photos]
      .filter((p) => p && typeof p.url === 'string' && p.url.length > 0)
      .sort((a, b) => _photoMillis(b) - _photoMillis(a));
  }, [project?.galleryPhotos]);

  const coverUrl = project?.imageUrl || null;

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  // Clear lightbox if the active photo gets deleted out from under us.
  useEffect(() => {
    if (lightboxIndex === null) return;
    if (lightboxIndex >= galleryPhotos.length) {
      setLightboxIndex(
        galleryPhotos.length > 0 ? galleryPhotos.length - 1 : null
      );
    }
  }, [galleryPhotos.length, lightboxIndex]);

  const handleUploadClick = useCallback(() => {
    if (!canEdit || uploading) return;
    fileInputRef.current?.click();
  }, [canEdit, uploading]);

  const handleFilesSelected = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []).filter((f) =>
        f.type.startsWith('image/')
      );
      // Reset the input so the same file can be re-picked.
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (files.length === 0) return;

      setUploading(true);
      setError(null);
      setUploadProgress({ done: 0, total: files.length });

      const uploadedUrls = [];
      let firstError = null;

      for (let i = 0; i < files.length; i++) {
        try {
          const url = await _uploadSinglePhoto(projectId, files[i]);
          await projectService.addProjectGalleryPhoto(projectId, {
            url,
            createdAt: Timestamp.now(),
          });
          uploadedUrls.push(url);
        } catch (err) {
          // Keep going so partial success still uploads.
          // eslint-disable-next-line no-console
          console.error('Photo upload failed:', err);
          if (!firstError) firstError = err;
        }
        setUploadProgress({ done: i + 1, total: files.length });
      }

      // iOS behaviour: if there's no cover yet, set the first uploaded photo
      // as the cover. Don't overwrite an existing cover the user picked.
      if (uploadedUrls.length > 0 && !project?.imageUrl) {
        try {
          await projectService.setProjectCoverPhoto(projectId, uploadedUrls[0]);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to auto-set cover photo:', err);
        }
      }

      setUploading(false);
      setUploadProgress(null);

      if (firstError && uploadedUrls.length === 0) {
        setError(`Upload failed: ${firstError.message || firstError}`);
      } else if (firstError) {
        setToast('Some photos failed to upload');
      } else {
        setToast(
          uploadedUrls.length === 1
            ? 'Photo uploaded'
            : `${uploadedUrls.length} photos uploaded`
        );
      }

      onProjectUpdate?.();
    },
    [projectId, project?.imageUrl, onProjectUpdate]
  );

  const handleSetCover = useCallback(
    async (photoUrl) => {
      if (!canEdit) return;
      setBusyAction(`cover:${photoUrl}`);
      try {
        await projectService.setProjectCoverPhoto(projectId, photoUrl);
        setToast('Cover photo updated');
        onProjectUpdate?.();
      } catch (err) {
        setError(`Failed to set cover: ${err.message || err}`);
      } finally {
        setBusyAction(null);
      }
    },
    [canEdit, projectId, onProjectUpdate]
  );

  const handleDelete = useCallback(
    async (photoUrl) => {
      if (!canEdit) return;
      setBusyAction(`delete:${photoUrl}`);
      try {
        await projectService.deleteProjectGalleryPhoto(projectId, photoUrl);
        setToast('Photo deleted');
        onProjectUpdate?.();
      } catch (err) {
        setError(`Failed to delete photo: ${err.message || err}`);
      } finally {
        setBusyAction(null);
        setPendingDeleteUrl(null);
      }
    },
    [canEdit, projectId, onProjectUpdate]
  );

  // Lightbox keyboard nav.
  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setLightboxIndex(null);
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((i) =>
          i === null ? null : Math.min(galleryPhotos.length - 1, i + 1)
        );
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((i) => (i === null ? null : Math.max(0, i - 1)));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, galleryPhotos.length]);

  const isEmpty = galleryPhotos.length === 0;

  return (
    <div className="project-photo-gallery">
      <div className="project-photo-gallery__header">
        <div className="project-photo-gallery__header-info">
          <h3 className="project-photo-gallery__title">Photos</h3>
          {!isEmpty && (
            <span className="project-photo-gallery__count">
              {galleryPhotos.length}{' '}
              {galleryPhotos.length === 1 ? 'photo' : 'photos'}
            </span>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            className="project-photo-gallery__upload-btn"
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {uploading
              ? uploadProgress
                ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                : 'Uploading…'
              : 'Upload photos'}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFilesSelected}
          style={{ display: 'none' }}
        />
      </div>

      {error && (
        <div className="project-photo-gallery__error" role="alert">
          {error}
          <button
            type="button"
            className="project-photo-gallery__error-close"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {isEmpty ? (
        <div className="project-photo-gallery__empty">
          <div className="project-photo-gallery__empty-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="project-photo-gallery__empty-text">
            No photos yet. Upload one to get started.
          </p>
          {canEdit && (
            <button
              type="button"
              className="project-photo-gallery__upload-btn project-photo-gallery__upload-btn--primary"
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Upload photos'}
            </button>
          )}
        </div>
      ) : (
        <div className="project-photo-gallery__grid">
          {galleryPhotos.map((photo, index) => (
            <PhotoTile
              key={photo.url}
              photo={photo}
              isCover={photo.url === coverUrl}
              canEdit={canEdit}
              busyAction={busyAction}
              onOpen={() => setLightboxIndex(index)}
              onSetCover={() => handleSetCover(photo.url)}
              onDelete={() => setPendingDeleteUrl(photo.url)}
            />
          ))}
        </div>
      )}

      {lightboxIndex !== null && galleryPhotos[lightboxIndex] && (
        <Lightbox
          photos={galleryPhotos}
          index={lightboxIndex}
          coverUrl={coverUrl}
          canEdit={canEdit}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((i) => Math.max(0, (i ?? 0) - 1))}
          onNext={() =>
            setLightboxIndex((i) =>
              Math.min(galleryPhotos.length - 1, (i ?? 0) + 1)
            )
          }
          onSetCover={handleSetCover}
          onDelete={(url) => setPendingDeleteUrl(url)}
          busyAction={busyAction}
        />
      )}

      {pendingDeleteUrl && (
        <ConfirmDialog
          isOpen
          title="Delete photo?"
          message="This will permanently remove the photo from the gallery. This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
          onConfirm={() => handleDelete(pendingDeleteUrl)}
          onCancel={() => setPendingDeleteUrl(null)}
        />
      )}

      {toast && (
        <div className="project-photo-gallery__toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

/**
 * Tile in the grid. Uses the `_400.jpg` thumbnail variant if it exists,
 * falling back to the full-resolution URL on 404 (via onError swap).
 */
function PhotoTile({
  photo,
  isCover,
  canEdit,
  busyAction,
  onOpen,
  onSetCover,
  onDelete,
}) {
  const initialSrc = _thumbUrlFor(photo.url) || photo.url;
  const [src, setSrc] = useState(initialSrc);
  const triedFallbackRef = useRef(false);

  const onError = useCallback(() => {
    if (!triedFallbackRef.current && src !== photo.url) {
      triedFallbackRef.current = true;
      setSrc(photo.url);
    }
  }, [src, photo.url]);

  const isSettingCover = busyAction === `cover:${photo.url}`;
  const isDeleting = busyAction === `delete:${photo.url}`;

  return (
    <div
      className={`project-photo-tile ${isCover ? 'is-cover' : ''}`}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <img
        src={src}
        alt=""
        className="project-photo-tile__img"
        loading="lazy"
        onError={onError}
      />
      {isCover && (
        <span className="project-photo-tile__cover-badge">Cover</span>
      )}
      {canEdit && (
        <div
          className="project-photo-tile__actions"
          onClick={(e) => e.stopPropagation()}
        >
          {!isCover && (
            <button
              type="button"
              className="project-photo-tile__action"
              onClick={onSetCover}
              disabled={isSettingCover}
              title="Set as cover"
            >
              {isSettingCover ? '…' : 'Set as cover'}
            </button>
          )}
          <button
            type="button"
            className="project-photo-tile__action project-photo-tile__action--danger"
            onClick={onDelete}
            disabled={isDeleting}
            title="Delete photo"
          >
            {isDeleting ? '…' : 'Delete'}
          </button>
        </div>
      )}
    </div>
  );
}

function Lightbox({
  photos,
  index,
  coverUrl,
  canEdit,
  onClose,
  onPrev,
  onNext,
  onSetCover,
  onDelete,
  busyAction,
}) {
  const photo = photos[index];
  if (!photo) return null;

  const isCover = photo.url === coverUrl;
  const isSettingCover = busyAction === `cover:${photo.url}`;
  const dateStr = _formatPhotoDate(photo.createdAt);

  return (
    <div
      className="project-photo-lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      <button
        type="button"
        className="project-photo-lightbox__close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
      >
        ×
      </button>

      {index > 0 && (
        <button
          type="button"
          className="project-photo-lightbox__nav project-photo-lightbox__nav--prev"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label="Previous photo"
        >
          ‹
        </button>
      )}
      {index < photos.length - 1 && (
        <button
          type="button"
          className="project-photo-lightbox__nav project-photo-lightbox__nav--next"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Next photo"
        >
          ›
        </button>
      )}

      <div
        className="project-photo-lightbox__stage"
        onClick={(e) => e.stopPropagation()}
      >
        <img src={photo.url} alt="" className="project-photo-lightbox__img" />
      </div>

      <div
        className="project-photo-lightbox__bar"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="project-photo-lightbox__meta">
          <span className="project-photo-lightbox__counter">
            {index + 1} / {photos.length}
          </span>
          {dateStr && (
            <span className="project-photo-lightbox__date">{dateStr}</span>
          )}
          {isCover && (
            <span className="project-photo-lightbox__cover-badge">
              Cover photo
            </span>
          )}
        </div>
        {canEdit && (
          <div className="project-photo-lightbox__actions">
            {!isCover && (
              <button
                type="button"
                className="project-photo-lightbox__btn"
                onClick={() => onSetCover(photo.url)}
                disabled={isSettingCover}
              >
                {isSettingCover ? 'Setting…' : 'Set as cover'}
              </button>
            )}
            <button
              type="button"
              className="project-photo-lightbox__btn project-photo-lightbox__btn--danger"
              onClick={() => onDelete(photo.url)}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Upload one image file to Firebase Storage, mirroring iOS's
 * `uploadSinglePhoto` storage path scheme and the `_400.jpg` client-generated
 * thumbnail. Returns the full-size download URL.
 */
async function _uploadSinglePhoto(projectId, file) {
  const photoId = _generatePhotoId();
  const basePath = `projects/${projectId}/gallery/${photoId}`;
  const fullPath = `${basePath}.jpg`;
  const thumbPath = `${basePath}_400.jpg`;

  // Compress + transcode the source to JPEG, matching iOS's 0.5 quality.
  const fullBlob = await _imageFileToJpegBlob(file, {
    maxDimension: null,
    quality: 0.5,
  });

  const fullRef = ref(storage, fullPath);
  await uploadBytes(fullRef, fullBlob, { contentType: 'image/jpeg' });
  const downloadUrl = await getDownloadURL(fullRef);

  // Best-effort thumbnail upload. If thumbnail generation or upload fails we
  // still return success — the grid falls back to the full-res URL.
  try {
    const thumbBlob = await _imageFileToJpegBlob(file, {
      maxDimension: 400,
      quality: 0.7,
    });
    const thumbRef = ref(storage, thumbPath);
    await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/jpeg' });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Thumbnail generation/upload failed; continuing:', err);
  }

  return downloadUrl;
}

/**
 * Read a File / Blob via <img>, draw it to canvas (optionally downscaling to
 * `maxDimension` on the long side), and return a JPEG Blob.
 */
function _imageFileToJpegBlob(file, { maxDimension, quality }) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (maxDimension && Math.max(width, height) > maxDimension) {
          const scale = maxDimension / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
              reject(new Error('Failed to encode JPEG'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          quality
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

function _generatePhotoId() {
  // Match iOS's `UUID().uuidString` (uppercase 8-4-4-4-12).
  return newUuid().toUpperCase();
}

/**
 * Derive the `_400.jpg` thumbnail download URL from a full-size download URL.
 * The download URL contains the encoded object path inside `/o/...`, so we
 * rewrite `.jpg` -> `_400.jpg` inside the encoded segment and return the
 * resulting URL. Returns null if the URL doesn't match the expected shape.
 */
function _thumbUrlFor(downloadUrl) {
  if (typeof downloadUrl !== 'string') return null;
  // Capture the encoded path between `/o/` and the query string.
  const match = downloadUrl.match(/^(.*\/o\/)([^?]+)(\?.*)?$/);
  if (!match) return null;
  const [, prefix, encodedPath, query = ''] = match;
  let decoded;
  try {
    decoded = decodeURIComponent(encodedPath);
  } catch {
    return null;
  }
  if (!/\.jpg$/i.test(decoded)) return null;
  const thumbDecoded = decoded.replace(/\.jpg$/i, '_400.jpg');
  return prefix + encodeURIComponent(thumbDecoded) + query;
}

function _photoMillis(photo) {
  const t = photo?.createdAt;
  if (!t) return 0;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.toDate === 'function') return t.toDate().getTime();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  if (typeof t === 'string' || t instanceof Date) {
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }
  return 0;
}

function _formatPhotoDate(timestamp) {
  const ms = _photoMillis({ createdAt: timestamp });
  if (!ms) return null;
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default ProjectPhotoGallery;
